import { Component, ErrorInfo, ReactNode } from 'react';
import { logError } from '../utils/logger.js';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AlertCircle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. When omitted, the default full-card fallback is shown. */
  fallback?: ReactNode;
  /** When any value in this array changes, the boundary resets (e.g. pass [location.pathname]). */
  resetKeys?: unknown[];
  /** Identifier for the error source (e.g. 'route:meta-dashboard'). */
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Report ONCE, with the React component stack folded into the context.
    // (logError forwards to Sentry; a second Sentry.captureException would
    // double-count the event and split its context — removed.)
    logError(
      this.props.name ? `ErrorBoundary.${this.props.name}` : 'ErrorBoundary.componentDidCatch',
      'React component error caught by Error Boundary',
      error,
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: this.props.name || 'GlobalErrorBoundary',
      }
    );
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Auto-recover when the reset keys change (e.g. user navigated to a new route).
    if (this.state.hasError && this.props.resetKeys && prevProps.resetKeys) {
      const changed =
        this.props.resetKeys.length !== prevProps.resetKeys.length ||
        this.props.resetKeys.some((k, i) => k !== prevProps.resetKeys![i]);
      if (changed) this.handleReset();
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[60vh] w-full flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full p-8 space-y-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertCircle className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Something went wrong</h1>
            </div>

            <div className="space-y-4">
              <p className="text-muted-foreground">
                We're sorry, but something unexpected happened. You can try again, or refresh the page.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 p-4 bg-muted rounded-md">
                  <summary className="cursor-pointer font-medium mb-2">
                    Error Details (Development Only)
                  </summary>
                  <div className="text-xs space-y-2 mt-2">
                    <div>
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="mt-1 whitespace-pre-wrap text-[10px] bg-background p-2 rounded">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack Trace:</strong>
                        <pre className="mt-1 whitespace-pre-wrap text-[10px] bg-background p-2 rounded">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="flex gap-3">
              <Button onClick={this.handleReset} className="flex-1">
                Try Again
              </Button>
              <Button onClick={this.handleRefresh} variant="outline" className="flex-1">
                Refresh Page
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
